<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("shipping_config.title") }}
                    </h6>
                    <nav
                        aria-label="breadcrumb"
                        class="d-none d-md-inline-block ml-md-4"
                    >
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button
                        size="md"
                        type="neutral"
                        v-if="isAccessShippingConfigWrite"
                        @click="handleAdd()"
                        >{{ $t("shipping_config.add_channel") }}</base-button
                    >
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <div>
                <card
                    class="no-border-card"
                    body-classes="px-0 pb-1"
                    footer-classes="pb-2"
                >
                    <div>
                        <el-table
                            :data="tableData"
                            row-key="sale_channel_id"
                            header-row-class-name="thead-light"
                            v-loading="loading"
                        >
                            <el-table-column
                                :label="$t('shipping_config.col_sale_channel')"
                                min-width="160"
                            >
                                <div slot-scope="{ row }">
                                    {{ row.sale_channel_name || ("#" + row.sale_channel_id) }}
                                </div>
                            </el-table-column>

                            <el-table-column
                                :label="$t('shipping_config.col_default')"
                                min-width="200"
                            >
                                <div slot-scope="{ row }">
                                    <template v-if="hasDefault(row)">
                                        {{ defaultText(row) }}
                                    </template>
                                    <span v-else class="text-muted">—</span>
                                </div>
                            </el-table-column>

                            <el-table-column
                                :label="$t('shipping_config.col_map')"
                                min-width="320"
                            >
                                <div slot-scope="{ row }">
                                    <el-tag
                                        v-for="(m, i) in (row.shipping_map || [])"
                                        :key="i"
                                        size="small"
                                        type="info"
                                        class="mr-1 mb-1"
                                    >
                                        {{ mapText(m) }}
                                    </el-tag>
                                    <span
                                        v-if="!row.shipping_map || row.shipping_map.length === 0"
                                        class="text-muted"
                                        >—</span
                                    >
                                </div>
                            </el-table-column>

                            <el-table-column
                                :label="$t('shipping_config.col_updated')"
                                min-width="160"
                            >
                                <div slot-scope="{ row }">
                                    {{ formatDate(row.updated_at) }}
                                </div>
                            </el-table-column>

                            <el-table-column
                                align="right"
                                :label="$t('actions')"
                                width="120"
                                v-if="isAccessShippingConfigWrite"
                            >
                                <div slot-scope="{ row }" class="d-flex">
                                    <base-button
                                        @click.native="handleEdit(row)"
                                        class="edit"
                                        type="warning"
                                        size="sm"
                                        icon
                                    >
                                        <i class="text-dtac-bt fa fa-edit"></i>
                                    </base-button>
                                </div>
                            </el-table-column>
                        </el-table>
                    </div>
                </card>
            </div>
        </div>

        <!-- Edit / Add dialog -->
        <el-dialog
            :title="dialogTitle"
            :visible.sync="showDialog"
            width="640px"
            append-to-body
        >
            <div v-loading="saving">
                <!-- Sale channel picker (add mode only) -->
                <div class="form-group" v-if="isAddMode">
                    <label class="form-control-label">{{
                        $t("shipping_config.col_sale_channel")
                    }}</label>
                    <el-select
                        v-model="form.sale_channel_id"
                        class="w-100"
                        filterable
                        :placeholder="$t('shipping_config.col_sale_channel')"
                    >
                        <el-option
                            v-for="opt in unconfiguredChannels"
                            :key="opt.id"
                            :label="opt.name"
                            :value="opt.id"
                        >
                        </el-option>
                    </el-select>
                </div>
                <div class="form-group" v-else>
                    <label class="form-control-label">{{
                        $t("shipping_config.col_sale_channel")
                    }}</label>
                    <div class="font-weight-bold">{{ form.sale_channel_name }}</div>
                </div>

                <!-- Default section -->
                <hr class="my-3" />
                <h6 class="heading-small text-muted mb-2">
                    {{ $t("shipping_config.default_section") }}
                </h6>
                <div class="row">
                    <div class="col-md-6 form-group">
                        <label class="form-control-label">{{
                            $t("shipping_config.payment_method")
                        }}</label>
                        <el-select
                            v-model="form.default_payment"
                            class="w-100"
                            filterable
                            clearable
                            placeholder=""
                        >
                            <el-option
                                v-for="pm in paymentMethods"
                                :key="pm.id"
                                :label="pm.name"
                                :value="pm.id"
                            >
                            </el-option>
                        </el-select>
                    </div>
                    <div class="col-md-6 form-group">
                        <label class="form-control-label">{{
                            $t("shipping_config.provider")
                        }}</label>
                        <el-select
                            v-model="form.default_provider"
                            class="w-100"
                            filterable
                            clearable
                            placeholder=""
                        >
                            <el-option
                                v-for="sp in shippingProviders"
                                :key="sp.id"
                                :label="sp.name"
                                :value="sp.id"
                            >
                            </el-option>
                        </el-select>
                    </div>
                </div>

                <!-- Mapping section -->
                <hr class="my-3" />
                <h6 class="heading-small text-muted mb-2">
                    {{ $t("shipping_config.map_section") }}
                </h6>
                <div
                    class="row align-items-end mb-2"
                    v-for="(mrow, idx) in form.shipping_map"
                    :key="idx"
                >
                    <div class="col-md-5 form-group mb-1">
                        <label class="form-control-label">{{
                            $t("shipping_config.payment_method")
                        }}</label>
                        <el-select
                            v-model="mrow.bo_payment_method_id"
                            class="w-100"
                            filterable
                            :placeholder="$t('shipping_config.payment_method')"
                        >
                            <el-option
                                v-for="pm in paymentMethods"
                                :key="pm.id"
                                :label="pm.name"
                                :value="pm.id"
                            >
                            </el-option>
                        </el-select>
                    </div>
                    <div class="col-md-5 form-group mb-1">
                        <label class="form-control-label">{{
                            $t("shipping_config.provider")
                        }}</label>
                        <el-select
                            v-model="mrow.shipping_provider_id"
                            class="w-100"
                            filterable
                            :placeholder="$t('shipping_config.provider')"
                        >
                            <el-option
                                v-for="sp in shippingProviders"
                                :key="sp.id"
                                :label="sp.name"
                                :value="sp.id"
                            >
                            </el-option>
                        </el-select>
                    </div>
                    <div class="col-md-2 form-group mb-1 text-right">
                        <base-button
                            @click.native="removeRow(idx)"
                            type="danger"
                            size="sm"
                            icon
                        >
                            <i class="fa fa-trash"></i>
                        </base-button>
                    </div>
                </div>

                <base-button
                    type="secondary"
                    size="sm"
                    @click="addRow()"
                >
                    <i class="fa fa-plus mr-1"></i>
                    {{ $t("shipping_config.add_row") }}
                </base-button>
            </div>

            <template slot="footer">
                <button
                    type="button"
                    class="btn btn-md btn-link"
                    @click="showDialog = false"
                >
                    {{ $t("shipping_config.cancel") }}
                </button>
                <button
                    type="button"
                    class="btn btn-md btn-primary"
                    :disabled="saving"
                    @click="handleSave()"
                >
                    {{ $t("shipping_config.save") }}
                </button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Dialog, Tag } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { mapGetters } from "vuex";
import swal from "sweetalert2";
import { ShippingConfigService } from "@/resource";

export default {
    components: {
        RouteBreadCrumb,
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Dialog.name]: Dialog,
        [Tag.name]: Tag
    },
    computed: {
        ...mapGetters({
            isAccessShippingConfig: "Auth/isAccessShippingConfig",
            isAccessShippingConfigWrite: "Auth/isAccessShippingConfigWrite"
        }),
        dialogTitle() {
            return this.isAddMode
                ? this.$t("shipping_config.add_channel")
                : this.$t("shipping_config.edit");
        },
        // Sale channels that are not yet configured (for add mode).
        unconfiguredChannels() {
            const configured = {};
            this.tableData.forEach(r => {
                configured[r.sale_channel_id] = true;
            });
            return this.saleChannels.filter(c => !configured[c.id]);
        }
    },
    data() {
        return {
            loading: false,
            saving: false,
            tableData: [],
            saleChannels: [],
            paymentMethods: [],
            shippingProviders: [],
            showDialog: false,
            isAddMode: false,
            form: {
                sale_channel_id: null,
                sale_channel_name: "",
                default_payment: null,
                default_provider: null,
                shipping_map: []
            }
        };
    },
    mounted() {
        this.loadOptions();
        this.loadList();
    },
    methods: {
        async loadList() {
            this.loading = true;
            try {
                const res = await ShippingConfigService.ShippingConfigList();
                this.tableData = (res.data && res.data.Data) || [];
            } catch (err) {
                this.showError(err);
            } finally {
                this.loading = false;
            }
        },
        async loadOptions() {
            try {
                const res = await ShippingConfigService.ShippingConfigOptions();
                const d = (res.data && res.data.Datum) || {};
                this.saleChannels = d.sale_channels || [];
                this.paymentMethods = d.payment_methods || [];
                this.shippingProviders = d.shipping_providers || [];
            } catch (err) {
                this.showError(err);
            }
        },
        // --- table renderers ---
        paymentName(id) {
            const pm = this.paymentMethods.find(p => p.id === id);
            return pm ? pm.name : "#" + id;
        },
        providerName(id) {
            const sp = this.shippingProviders.find(p => p.id === id);
            return sp ? sp.name : "#" + id;
        },
        hasDefault(row) {
            const d = row.default || {};
            return (
                (d.bo_payment_method_id && d.bo_payment_method_id > 0) ||
                (d.shipping_provider_id && d.shipping_provider_id > 0)
            );
        },
        defaultText(row) {
            const d = row.default || {};
            const pm = d.bo_payment_method_name || (d.bo_payment_method_id ? this.paymentName(d.bo_payment_method_id) : this.$t("shipping_config.none"));
            const sp = d.shipping_provider_name || (d.shipping_provider_id ? this.providerName(d.shipping_provider_id) : this.$t("shipping_config.none"));
            return pm + " → " + sp;
        },
        mapText(m) {
            const pm = m.bo_payment_method_name || this.paymentName(m.bo_payment_method_id);
            const sp = m.shipping_provider_name || this.providerName(m.shipping_provider_id);
            return pm + " → " + sp;
        },
        formatDate(v) {
            if (!v) return "—";
            const dt = new Date(v);
            if (isNaN(dt.getTime())) return "—";
            return dt.toLocaleString();
        },
        // --- dialog control ---
        handleAdd() {
            this.isAddMode = true;
            this.form = {
                sale_channel_id: null,
                sale_channel_name: "",
                default_payment: null,
                default_provider: null,
                shipping_map: []
            };
            this.showDialog = true;
        },
        handleEdit(row) {
            this.isAddMode = false;
            const d = row.default || {};
            this.form = {
                sale_channel_id: row.sale_channel_id,
                sale_channel_name: row.sale_channel_name || "#" + row.sale_channel_id,
                default_payment: d.bo_payment_method_id || null,
                default_provider: d.shipping_provider_id || null,
                shipping_map: (row.shipping_map || []).map(m => ({
                    bo_payment_method_id: m.bo_payment_method_id,
                    shipping_provider_id: m.shipping_provider_id
                }))
            };
            this.showDialog = true;
        },
        addRow() {
            this.form.shipping_map.push({
                bo_payment_method_id: null,
                shipping_provider_id: null
            });
        },
        removeRow(idx) {
            this.form.shipping_map.splice(idx, 1);
        },
        // --- save ---
        warn(msg) {
            swal.fire({
                title: msg,
                icon: "warning",
                confirmButtonClass: "btn btn-warning btn-fill",
                buttonsStyling: false
            });
        },
        validate() {
            if (this.isAddMode && !this.form.sale_channel_id) {
                this.warn(this.$t("shipping_config.col_sale_channel"));
                return false;
            }
            const seen = {};
            for (const m of this.form.shipping_map) {
                if (!m.bo_payment_method_id || !m.shipping_provider_id) {
                    // incomplete rows are ignored by BE contract; block to be safe
                    this.warn(this.$t("shipping_config.map_section"));
                    return false;
                }
                if (seen[m.bo_payment_method_id]) {
                    this.warn(this.$t("shipping_config.dup_payment_err"));
                    return false;
                }
                seen[m.bo_payment_method_id] = true;
            }
            return true;
        },
        async handleSave() {
            if (!this.validate()) return;
            const payload = {
                default: {
                    bo_payment_method_id: this.form.default_payment || 0,
                    shipping_provider_id: this.form.default_provider || 0
                },
                shipping_map: this.form.shipping_map.map(m => ({
                    bo_payment_method_id: m.bo_payment_method_id,
                    shipping_provider_id: m.shipping_provider_id
                }))
            };
            this.saving = true;
            try {
                await ShippingConfigService.ShippingConfigUpdate(
                    this.form.sale_channel_id,
                    payload
                );
                this.showDialog = false;
                // BE reloads the in-memory ShippingConfig immediately on save,
                // so no server restart is needed for the new mapping to apply.
                swal.fire({
                    title: this.$t("shipping_config.save_success"),
                    icon: "success",
                    confirmButtonClass: "btn btn-success btn-fill",
                    buttonsStyling: false
                });
                this.loadList();
            } catch (err) {
                this.showError(err);
            } finally {
                this.saving = false;
            }
        },
        showError(err) {
            const data = (err && err.response && err.response.data) || {};
            swal.fire({
                title: data.Msg ? this.$t(data.Msg) : this.$t("save_failed"),
                text: this.getErrorText ? this.getErrorText(data) : "",
                icon: "error",
                confirmButtonClass: "btn btn-danger btn-fill",
                buttonsStyling: false
            });
        }
    }
};
</script>

<style>
.no-border-card .card-footer {
    border-top: 0;
}
</style>
